import { FileSearch, CheckCircle, Shield, Target } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const CAFAssessmentsService = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="pt-24 pb-16 bg-secondary/30">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="max-w-4xl mx-auto text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
              <FileSearch className="w-8 h-8 text-accent" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              CAF Assessments
            </h1>
            <p className="text-xl text-muted-foreground">
              Evaluate your cyber security maturity against the NCSC Cyber Assessment Framework
            </p>
          </div>

          {/* Overview Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-6">What is the Cyber Assessment Framework?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              The NCSC Cyber Assessment Framework (CAF) is a comprehensive framework designed to help organisations assess and improve their cyber security posture. It provides a systematic approach to evaluating security controls and identifying areas for improvement.
            </p>
            <p className="text-lg text-muted-foreground">
              Our CAF assessment services help you understand where you stand, identify gaps, and develop actionable plans to enhance your security maturity.
            </p>
          </div>

          {/* Benefits Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">Why Choose Our CAF Assessment Services?</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Expert Evaluation</h3>
                  <p className="text-muted-foreground">
                    Our experienced assessors provide thorough evaluation against all CAF objectives and indicators.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Shield className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Comprehensive Coverage</h3>
                  <p className="text-muted-foreground">
                    Assessment across all 14 principles of the CAF, from governance to security monitoring.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Target className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Actionable Insights</h3>
                  <p className="text-muted-foreground">
                    Clear recommendations and prioritised roadmap for improving your security maturity.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <FileSearch className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2">Detailed Reporting</h3>
                  <p className="text-muted-foreground">
                    Comprehensive reports showing your current maturity level and gaps against best practice.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* CAF Principles Section */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">CAF Assessment Areas</h2>
            <div className="space-y-4">
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Objective A: Managing Security Risk</h3>
                <p className="text-muted-foreground">
                  Governance, risk management, and security roles and responsibilities
                </p>
              </div>
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Objective B: Protecting Against Cyber Attack</h3>
                <p className="text-muted-foreground">
                  Asset management, supply chain security, identity and access control, data security, and vulnerability management
                </p>
              </div>
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Objective C: Detecting Cyber Security Events</h3>
                <p className="text-muted-foreground">
                  Security monitoring and proactive threat discovery
                </p>
              </div>
              <div className="p-6 border rounded-lg bg-card">
                <h3 className="text-xl font-semibold mb-2">Objective D: Minimising the Impact of Cyber Security Incidents</h3>
                <p className="text-muted-foreground">
                  Incident response, recovery, and lessons learned
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="max-w-4xl mx-auto text-center">
            <div className="p-8 border rounded-lg bg-card">
              <h2 className="text-3xl font-bold mb-4">Ready to Assess Your Security Maturity?</h2>
              <p className="text-lg text-muted-foreground mb-6">
                Let's discuss how our CAF assessment services can help you understand and improve your cyber security posture.
              </p>
              <Link to="/#contact">
                <Button size="lg" className="bg-accent hover:bg-accent/90">
                  Get in Touch
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CAFAssessmentsService;
